import { useState } from "react";
import { Box, Typography } from "@mui/material";

// Full-width multi-line income graph — one line per community over time.
// Dependency-free inline SVG (same approach as the dashboard charts): the
// smooth curves live in a 0..100 viewBox with non-scaling strokes, and every
// label / dot / tooltip is an HTML overlay positioned with physical inline
// styles so stylis-plugin-rtl can't desync them from the SVG in Hebrew.
//
// series: [{ name, color, values: number[] }]. labels: string[] (per point).
const PAD_X = 2;
const PAD_Y = 8;

// Round a number to a "nice" 1/2/5 × 10^n value (for clean axis ticks).
const niceNum = (x, round) => {
  if (x <= 0) return 1;
  const exp = Math.floor(Math.log10(x));
  const base = Math.pow(10, exp);
  const f = x / base;
  let nf;
  if (round) nf = f < 1.5 ? 1 : f < 3 ? 2 : f < 7 ? 5 : 10;
  else nf = f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10;
  return nf * base;
};

const shortMoney = (cur) => (v) => {
  if (v >= 1000) return `${cur}${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}k`;
  return `${cur}${v}`;
};

// Catmull-Rom → cubic-bezier smoothing for a professional curved line.
const smoothPath = (pts) => {
  if (pts.length < 2) return "";
  let d = `M ${pts[0].x.toFixed(2)} ${pts[0].y.toFixed(2)}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] || p2;
    const c1x = p1.x + (p2.x - p0.x) / 6;
    const c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6;
    const c2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${c1x.toFixed(2)} ${c1y.toFixed(2)}, ${c2x.toFixed(2)} ${c2y.toFixed(2)}, ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`;
  }
  return d;
};

const IncomeGraph = ({ series = [], labels = [], currencySymbol = "₪", height = 300, hint = "" }) => {
  const [hover, setHover] = useState(null);
  const [active, setActive] = useState(null); // legend-selected series name

  if (!series.length || labels.length < 2) return null;

  const n = labels.length;
  const dataMax = Math.max(1, ...series.flatMap((s) => s.values));
  const step = niceNum(niceNum(dataMax, false) / 4, true);
  const niceMax = Math.max(step, Math.ceil(dataMax / step) * step);
  const levels = [];
  for (let v = 0; v <= niceMax + step / 2; v += step) levels.push(v);
  const fmtK = shortMoney(currencySymbol);
  const fullMoney = (v) => `${currencySymbol}${Number(v).toLocaleString("en-US")}`;

  const xAt = (i) => PAD_X + (i / (n - 1)) * (100 - PAD_X * 2);
  const yAt = (v) => PAD_Y + (1 - v / niceMax) * (100 - PAD_Y * 2);
  const dimmed = (name) => active && active !== name;
  // The legend doubles as the readout: it shows values for the hovered/tapped
  // month, falling back to the latest month. No floating tooltip to clip.
  const readoutIdx = hover != null ? hover : n - 1;

  return (
    <Box sx={{ maxWidth: "100%", overflow: "hidden" }}>
      <Box style={{ position: "relative", paddingLeft: "52px", paddingBottom: "22px" }}>
        <Box sx={{ position: "relative", height: `${height}px` }}>
          <svg
            viewBox="0 0 100 100"
            width="100%"
            height="100%"
            preserveAspectRatio="none"
            style={{ display: "block", overflow: "visible" }}
            aria-hidden="true"
          >
            {levels.map((v, i) => {
              const y = yAt(v);
              return (
                <line key={i} x1={PAD_X} x2={100 - PAD_X} y1={y} y2={y} stroke="rgba(234, 234, 234, 1)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
              );
            })}
            {hover != null && (
              <line x1={xAt(hover)} x2={xAt(hover)} y1={PAD_Y} y2={100 - PAD_Y} stroke="rgba(47, 43, 61, 0.25)" strokeWidth="1" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />
            )}
            {series.map((s) => (
              <path
                key={s.name}
                d={smoothPath(s.values.map((v, i) => ({ x: xAt(i), y: yAt(v) })))}
                fill="none"
                stroke={s.color}
                strokeWidth={active === s.name ? 2.6 : 2}
                strokeLinejoin="round"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
                opacity={dimmed(s.name) ? 0.12 : 0.95}
              />
            ))}
          </svg>

          {/* y-axis labels (nice-round numbers) */}
          {levels.map((v, i) => (
            <Typography
              key={i}
              style={{ position: "absolute", left: "-52px", top: `${yAt(v)}%`, width: "46px" }}
              sx={{ transform: "translateY(-50%)", textAlign: "end", fontSize: "10px", color: "rgba(71, 84, 103, 0.85)", whiteSpace: "nowrap" }}
            >
              {fmtK(v)}
            </Typography>
          ))}

          {/* a dot on every data point of every line */}
          {series.map((s) =>
            s.values.map((v, i) => {
              const big = hover === i;
              const size = big ? 9 : 6;
              return (
                <Box
                  key={`${s.name}-${i}`}
                  style={{ position: "absolute", left: `${xAt(i)}%`, top: `${yAt(v)}%`, transform: "translate(-50%, -50%)" }}
                  sx={{ width: size, height: size, borderRadius: "50%", backgroundColor: "#fff", border: `2px solid ${s.color}`, opacity: dimmed(s.name) ? 0.12 : 1, pointerEvents: "none", transition: "width .1s, height .1s" }}
                />
              );
            }),
          )}

          {/* hover / tap hit-columns (tap works on touch; the legend reads out) */}
          {labels.map((label, i) => (
            <Box
              key={i}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
              onClick={() => setHover(i)}
              style={{ position: "absolute", left: `${xAt(i)}%`, top: 0, height: "100%", transform: "translateX(-50%)" }}
              sx={{ width: `${(100 - PAD_X * 2) / n}%`, cursor: "pointer" }}
            />
          ))}

          {/* x labels — first start-anchored, last end-anchored so edge labels never clip */}
          {labels.map((label, i) => (
            <Typography
              key={i}
              style={{ position: "absolute", bottom: "-22px", left: `${xAt(i)}%`, transform: `translateX(${i === 0 ? "0%" : i === n - 1 ? "-100%" : "-50%"})` }}
              sx={{ fontSize: "10px", color: "rgba(71, 84, 103, 0.85)", whiteSpace: "nowrap" }}
            >
              {label}
            </Typography>
          ))}

        </Box>
      </Box>

      {/* Legend = readout. Shows each community's value for the hovered/tapped
          month (latest by default); click a row to highlight just that line. */}
      <Box sx={{ mt: "10px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mb: "8px" }}>
          <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "rgba(47, 43, 61, 0.9)" }}>{labels[readoutIdx]}</Typography>
          {hover == null && hint && <Typography sx={{ fontSize: "11px", color: "rgba(71, 84, 103, 0.7)" }}>· {hint}</Typography>}
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))" }, gap: "4px 16px" }}>
          {series.map((s) => (
            <Box
              key={s.name}
              component="button"
              type="button"
              onClick={() => setActive((a) => (a === s.name ? null : s.name))}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                border: "none",
                background: "none",
                fontFamily: "inherit",
                cursor: "pointer",
                padding: "4px 6px",
                borderRadius: "6px",
                opacity: dimmed(s.name) ? 0.4 : 1,
                "&:hover": { backgroundColor: "rgba(245, 246, 250, 1)" },
              }}
            >
              <Box sx={{ width: 10, height: 10, borderRadius: "3px", backgroundColor: s.color, flexShrink: 0 }} />
              <Typography sx={{ fontSize: "12px", fontWeight: active === s.name ? 600 : 400, color: "rgba(62, 63, 76, 0.85)", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "start" }}>
                {s.name}
              </Typography>
              <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "rgba(47, 43, 61, 0.9)", whiteSpace: "nowrap" }}>{fullMoney(s.values[readoutIdx])}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default IncomeGraph;
